import GlobalStyles from '@mui/material/GlobalStyles'
import { lighten } from '@mui/material/styles'
import React from 'react'

import { NOTIFICATION_SEVERITY, type NotificationSeverity } from 'types/modules/notification-center/notification'

import { NOTIFICATION_TOAST_WIDTH } from './notificationCenterConstants'

import type { Theme } from '@mui/material/styles'

/** `lighten`, not `alpha`: this replaces the theme's opaque surface, so translucency would show the page. */
const TINT_STRENGTH = 0.92

const RAIL_WIDTH = 3

/** The class each notification toast carries, plus a per-severity variant that tints the whole surface. */
export const NOTIFICATION_TOAST_CLASS = 'notification-toast'

export const notificationToastClass = (severity: NotificationSeverity): string =>
  `${NOTIFICATION_TOAST_CLASS} ${NOTIFICATION_TOAST_CLASS}--${severity}`

/**
 * Mirrors the grid's unread cards: tinted surface, severity rail down the left. On the toast itself, not
 * a card inside it — a tinted block ringed by white padding reads as a component that didn't fit — and
 * keyed on our own class rather than toastify's internal type classes.
 */
const toastStyles = (theme: Theme) => {
  const surface = (severity: NotificationSeverity) => ({
    backgroundColor: lighten(theme.palette[severity].main, TINT_STRENGTH),
    borderLeftColor: theme.palette[severity].main,
  })

  return {
    [`.${NOTIFICATION_TOAST_CLASS}`]: {
      /**
       * The toast sizes itself from this variable — `.Toastify__toast { width: var(--toastify-toast-width) }`
       * — so widening the container alone leaves every toast at its 320px default.
       */
      '--toastify-toast-width': NOTIFICATION_TOAST_WIDTH,
      // Toastify's own narrow-screen rule sets that variable to 100%; ours would otherwise win over it.
      maxWidth: '100%',
      // Toastify's light theme sets its own grey text colour, which anything without an explicit
      // colour inherits — that is why an untinted title read differently here than in the popover.
      color: theme.palette.text.primary,
      borderLeftStyle: 'solid',
      borderLeftWidth: RAIL_WIDTH,
      // A step tighter than toastify's own 6px, closer to the cards this borrows from.
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.25, 1, 1.25, 1.5),
      // The pending bar anchors to the toast's own edge rather than to the padded content box,
      // and the clip keeps it inside the rounded corners.
      position: 'relative',
      overflow: 'hidden',
    },
    [`.${NOTIFICATION_TOAST_CLASS} .Toastify__toast-body`]: {
      margin: 0,
      padding: 0,
      position: 'static',
      // Fill the row, so the summary wraps against the toast's width and the close button has the
      // remaining space to be pushed into.
      flex: '1 1 auto',
      minWidth: 0,
    },
    [`.${NOTIFICATION_TOAST_CLASS}--${NOTIFICATION_SEVERITY.INFO}`]: surface(NOTIFICATION_SEVERITY.INFO),
    [`.${NOTIFICATION_TOAST_CLASS}--${NOTIFICATION_SEVERITY.SUCCESS}`]: surface(NOTIFICATION_SEVERITY.SUCCESS),
    [`.${NOTIFICATION_TOAST_CLASS}--${NOTIFICATION_SEVERITY.WARNING}`]: surface(NOTIFICATION_SEVERITY.WARNING),
    [`.${NOTIFICATION_TOAST_CLASS}--${NOTIFICATION_SEVERITY.ERROR}`]: surface(NOTIFICATION_SEVERITY.ERROR),
  }
}

const NotificationToastStyles = () => <GlobalStyles styles={toastStyles} />

export default NotificationToastStyles
